import { useCallback, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "@its-engineering-and-development/ui-foundation/components";
import type { TablePageInfo } from "@its-engineering-and-development/ui-foundation/components";
import { useNotifications } from "@its-engineering-and-development/ui-foundation/providers";
import type { SortDirection } from "@/components/base/column-filter-popover";
import { useActivePolicyCategories } from "@app/hooks/use-categories";
import { isMileage } from "@app/constants/expense-categories";
import { useCreatePolicy, useReceiptPolicies, useUpdatePolicy } from "@app/hooks/use-policy";
import type { CreatePolicyInput, ReceiptPoliciesColumnFilter, ReceiptPoliciesSortBy, UpdatePolicyInput } from "@app/hooks/use-policy";
import { LOST_RECEIPT_ESCALATION_LEVEL } from "@app/types/policy";
import { ACTIONS } from "@/auth";
import { useCan } from "@/hooks/use-can";
import { PolicyPageError } from "../policy-table-header";
import { DataTableShell } from "@app/components/base/data-table/data-table";
import { PolicyStatusBadge } from "../policy-status-badge";
import { usePolicyTableColumnHeader } from "../use-policy-table-column-header";
import { ReceiptDrawer, type ReceiptPolicyDraft } from "./receipt-drawer";
import { formatDate } from "@/utils/date";

const RECEIPT_SEARCH_HISTORY_KEY = "expense-admin:receipt-policy-column-search-history";

type ReceiptColumnKey = "ruleName" | "status" | "requiredAmount" | "updatedBy" | "changedDate";

const RECEIPT_STATUS_FILTER_OPTIONS = [
    { label: "Active", value: "active" },
    { label: "Deactivated", value: "inactive" },
];

// Maps UI column key → API sort field name (only server-sortable columns)
const COLUMN_SORT_FIELD: Partial<Record<ReceiptColumnKey, ReceiptPoliciesSortBy>> = {
    ruleName: "name",
};

function buildReceiptApiFilter(applied: Partial<Record<ReceiptColumnKey, string>>): ReceiptPoliciesColumnFilter {
    const filter: ReceiptPoliciesColumnFilter = {};
    if (applied.ruleName) filter.name = applied.ruleName;
    if (applied.status) filter.status = applied.status;
    return filter;
}

function IconPlus() {
    return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" d="M12 5v14M5 12h14" />
        </svg>
    );
}

function parseReceiptAmount(value: string): number {
    const parsed = parseFloat(value.replace(/[^0-9.]/g, ""));
    return Number.isFinite(parsed) ? parsed : 0;
}

export function ReceiptPage() {
    const [activeFilters, setActiveFilters] = useState<Partial<Record<ReceiptColumnKey, string>>>({});
    const [sort, setSort] = useState<{ by: ReceiptColumnKey; order: SortDirection } | null>(null);
    const {
        policies: rules,
        pageInfo,
        currentPage,
        isLoading,
        error,
        setPage,
        setSort: setApiSort,
        setColumnFilter: setApiColumnFilter,
        refresh,
    } = useReceiptPolicies();
    const { createPolicy, isLoading: isCreatingPolicy } = useCreatePolicy();
    const { updatePolicy, isLoading: isUpdatingPolicy } = useUpdatePolicy();
    const { showSuccess, showError } = useNotifications();
    const canWrite = useCan(ACTIONS.policy.receipt.write);
    const { categories, isLoading: isLoadingCategories, error: categoriesError } = useActivePolicyCategories();
    const isSaving = isCreatingPolicy || isUpdatingPolicy;
    const [selectedRuleId, setSelectedRuleId] = useState<string | null>(null);
    const [isCreating, setIsCreating] = useState(false);

    const handleFilterApply = useCallback(
        (column: ReceiptColumnKey, value: string) => {
            const next = { ...activeFilters, [column]: value };
            setActiveFilters(next);
            setApiColumnFilter(buildReceiptApiFilter(next));
        },
        [activeFilters, setApiColumnFilter],
    );

    const handleFilterClear = useCallback(
        (column: ReceiptColumnKey) => {
            const next = { ...activeFilters };
            delete next[column];
            setActiveFilters(next);
            setApiColumnFilter(buildReceiptApiFilter(next));
        },
        [activeFilters, setApiColumnFilter],
    );

    const handleSortChange = useCallback(
        (column: ReceiptColumnKey) => {
            let next: { by: ReceiptColumnKey; order: SortDirection } | null;
            if (!sort || sort.by !== column) next = { by: column, order: "asc" };
            else if (sort.order === "asc") next = { by: column, order: "desc" };
            else next = null;
            setSort(next);
            const apiField = next ? COLUMN_SORT_FIELD[next.by] : undefined;
            setApiSort(next && apiField ? { sortBy: apiField, sortOrder: next.order } : null);
        },
        [sort, setApiSort],
    );

    const colHeader = usePolicyTableColumnHeader(RECEIPT_SEARCH_HISTORY_KEY, activeFilters, sort, handleFilterApply, handleFilterClear, handleSortChange);

    const hasActiveFilters = Object.keys(activeFilters).length > 0;

    const tablePageInfo: TablePageInfo | null = pageInfo
        ? {
              totalCount: pageInfo.totalCount,
              offset: currentPage * pageInfo.limit,
              limit: pageInfo.limit,
              hasNextPage: pageInfo.hasNextPage,
          }
        : null;

    const selectedRule = useMemo(() => rules.find((rule) => rule.id === selectedRuleId) ?? null, [rules, selectedRuleId]);

    const closeDrawer = () => {
        setSelectedRuleId(null);
        setIsCreating(false);
    };

    const handleSaveRule = async (draft: ReceiptPolicyDraft) => {
        if (!canWrite) return;
        if (isLoadingCategories) {
            showError("Categories still loading", "Please wait a moment and try again.");
            return;
        }
        if (categoriesError) {
            showError("Could not load categories", "Failed to load category data. Please refresh the page and try again.");
            return;
        }
        const mileageCategoryId = categories.find((c) => isMileage(c.legacyId))?.categoryId;
        let expression = `expense.amount >= ${parseReceiptAmount(draft.requireReceiptAtOrAbove)} && expense.receiptStatus != "ATTACHED"`;
        if (draft.lostReceiptEscalationLevel === LOST_RECEIPT_ESCALATION_LEVEL.Initial) {
            expression += ` && !(expense.lostReceiptDeclaration != null && expense.lostReceiptDeclaration.declared && expense.lostReceiptDeclaration.hasInitials)`;
        } else if (draft.lostReceiptEscalationLevel === LOST_RECEIPT_ESCALATION_LEVEL.Explanation) {
            expression += ` && !(expense.lostReceiptDeclaration != null && expense.lostReceiptDeclaration.declared && expense.lostReceiptDeclaration.hasInitials && expense.lostReceiptDeclaration.hasExplanation)`;
        }
        const rulesPayload = {
            type: "cel" as const,
            categoryIds: ["*"],
            ...(mileageCategoryId ? { excludeCategoryIds: [mileageCategoryId] } : {}),
            expression,
            params: {
                receiptCollectionRules: {
                    lostReceiptProtocol: { escalationLevel: draft.lostReceiptEscalationLevel },
                },
            },
        };
        try {
            if (isCreating) {
                const input: CreatePolicyInput = {
                    name: draft.ruleName,
                    status: "active",
                    policyType: "receipt",
                    rules: rulesPayload,
                    actions: [{ type: "autoBlock" }],
                };
                await createPolicy(input);
                showSuccess("Receipt policy created", `"${draft.ruleName}" was created.`);
            } else if (selectedRuleId) {
                const input: UpdatePolicyInput = {
                    name: draft.ruleName,
                    status: "active",
                    policyType: "receipt",
                    rules: rulesPayload,
                    actions: [{ type: "autoBlock" }],
                };
                await updatePolicy(selectedRuleId, input);
                showSuccess("Receipt policy updated", `"${draft.ruleName}" was updated.`);
            }
            closeDrawer();
            refresh();
        } catch {
            showError("Failed to save receipt policy", "Something went wrong. Please try again.");
        }
    };

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-900">Receipt Policy</h1>
                    <p className="mt-2 text-sm text-gray-600">Update or add a receipt policy and set autoapproval rules, if applicable for your program.</p>
                </div>

                <div className="flex shrink-0 items-center gap-3">
                    {canWrite && (
                        <Button
                            color="primary"
                            size="md"
                            onPress={() => {
                                setSelectedRuleId(null);
                                setIsCreating(true);
                            }}
                            iconLeading={IconPlus}
                        >
                            Add Receipt Policy
                        </Button>
                    )}
                </div>
            </div>

            {error && <PolicyPageError message={error} />}

            <DataTableShell
                tabName="receipt policy"
                minWidth="820px"
                pageInfo={tablePageInfo}
                onPageChange={setPage}
                isLoading={isLoading}
                loadingDescription="Please wait while the receipt policies load."
                isEmpty={rules.length === 0}
                hasActiveFilters={hasActiveFilters}
                emptyMessage={hasActiveFilters ? "No receipts match the current filters." : "No receipts found."}
                header={
                    <thead className="relative z-30 border-b border-gray-200 bg-gray-50 text-left">
                        <tr>
                            {colHeader("ruleName", "Rule Name")}
                            {colHeader("status", "Status", { options: RECEIPT_STATUS_FILTER_OPTIONS, sortable: false })}
                            {colHeader("requiredAmount", "Required Amount", { filterable: false, sortable: false })}
                            {colHeader("updatedBy", "Updated By", { filterable: false, sortable: false })}
                            {colHeader("changedDate", "Changed Date", { filterable: false, sortable: false })}
                        </tr>
                    </thead>
                }
            >
                <tbody className="divide-y divide-gray-100">
                    {rules.map((rule) => (
                        <tr key={rule.id} className="cursor-pointer hover:bg-gray-50" onClick={() => setSelectedRuleId(rule.id)}>
                            <td className="px-4 py-3 font-medium text-blue-600">{rule.ruleName}</td>
                            <td className="px-4 py-3">
                                <PolicyStatusBadge status={rule.status} />
                            </td>
                            <td className="px-4 py-3 text-gray-700">{rule.requiredAmount}</td>
                            <td className="px-4 py-3 text-gray-700">{rule.updatedBy}</td>
                            <td className="px-4 py-3 text-gray-700">{formatDate(rule.changedDate)}</td>
                        </tr>
                    ))}
                </tbody>
            </DataTableShell>

            {(isCreating || selectedRule) &&
                createPortal(
                    <ReceiptDrawer
                        rule={isCreating ? undefined : (selectedRule ?? undefined)}
                        onClose={closeDrawer}
                        onSave={handleSaveRule}
                        isSaving={isSaving}
                        canEdit={canWrite}
                    />,
                    document.body,
                )}
        </div>
    );
}
